import { CartAddEvent, CartErrorEvent } from '@theme/events';
const TARGETS = {
  driver: ['fairway', 'utility'], fairway: ['utility', 'driver'],
  utility: ['fairway', 'driver'], caddy: ['travel'],
};
function eligible(product) {
  return product?.available && !(product.tags || []).some(tag =>
    ['HIROO ONLY', 'SECRET', '入荷販売保留'].includes(tag)
  );
}
function matchingCandidates(index, items, anchorId) {
  const metadata = new Map(index.products.map(p => [String(p.id), p]));
  const cartIds = new Set(items.map(p => String(p.product_id)));
  const cartCategories = new Set(items.map(p => metadata.get(String(p.product_id))?.category));
  const candidates = new Map();
  for (const item of items) {
    const source = metadata.get(String(item.product_id));
    if (!source) continue;
    const targets = TARGETS[source.category] || [];
    for (const product of index.products) {
      const priority = targets.indexOf(product.category);
      if (priority < 0 || cartIds.has(String(product.id)) || cartCategories.has(product.category)) continue;
      const travel = source.category === 'caddy' && product.category === 'travel';
      if (!travel && source.designKey !== product.designKey) continue;
      const score = 1000 - priority * 20 + (cartCategories.has(product.category) ? 0 : 200)
        + (String(item.product_id) === String(anchorId) ? 5 : 0);
      const candidate = {...product, score, sourceId: String(item.product_id), reason: travel ? 'travel' : 'title'};
      if (!candidates.has(String(product.id)) || candidates.get(String(product.id)).score < score) candidates.set(String(product.id), candidate);
    }
  }
  return [...candidates.values()].sort((a,b) => b.score-a.score || a.handle.localeCompare(b.handle));
}
function diversify(products, limit) {
  const result=[], used=new Set();
  for (const p of products) if (!used.has(p.category)) { result.push(p); used.add(p.category); }
  for (const p of products) if (!result.includes(p)) result.push(p);
  return result.slice(0,limit);
}

const EVENT_NAME=CartAddEvent.eventName;
const STORAGE_KEY='monzee:last-cart-product-id';
let indexPromise;
document.addEventListener(EVENT_NAME, event => {
  try { if(event.detail?.data?.productId) sessionStorage.setItem(STORAGE_KEY,String(event.detail.data.productId)); } catch {}
}, {capture:true});
class MonzeeCartRecommendations extends HTMLElement {
  #abortController; #cart; #anchor; #generation=0;
  connectedCallback(){window.setTimeout(()=>this.hydrate(),0);}
  disconnectedCallback(){this.#generation++;this.#abortController?.abort();}
  hydrate(){ if(this.isConnected) this.refresh(); }
  #route(path){return `${window.Shopify?.routes?.root || '/'}${path}`.replace(/\/+/g,'/');}
  async #fetchJson(url,signal){
    const response=await fetch(url,{headers:{Accept:'application/json'},signal});
    if(!response.ok) throw new Error(`Product information unavailable (${response.status})`);
    return response.json();
  }
  async refresh(){
    // During sequential releases an older snippet can still be present.
    // Recommendation failures never change the normal cart or checkout.
    if(!this.dataset.indexUrl || !this.querySelector('[data-monzee-recommendation-list]')){this.hidden=true;return;}
    const generation=++this.#generation;
    this.#abortController?.abort(); this.#abortController=new AbortController();
    const signal=this.#abortController.signal;
    this.setAttribute('data-loading','true'); this.removeAttribute('data-ready');
    try {
      const cart=await this.#fetchJson(this.#route('cart.js'),signal);
      if(!cart.items?.length){this.hidden=true;return;}
      let remembered=''; try{ remembered=sessionStorage.getItem(STORAGE_KEY)||''; }catch{}
      const anchor=cart.items.find(p=>String(p.product_id)===remembered)||cart.items[0];
      if(!indexPromise) indexPromise=this.#fetchJson(this.dataset.indexUrl).catch(error=>{indexPromise=null;throw error;});
      const index=await indexPromise;
      const candidates=matchingCandidates(index,cart.items,anchor.product_id);
      const limit=Math.min(Number(this.dataset.limit)||12,12);
      // Fetch a bounded number of candidates concurrently; current market prices
      // and stock are never taken from the index snapshot.
      const verified=[];
      for(let offset=0;offset<candidates.length && verified.length<limit*3;offset+=6){
        const batch=await Promise.all(candidates.slice(offset,offset+6).map(async candidate=>{
          try {
            const current=await this.#fetchJson(this.#route(`products/${encodeURIComponent(candidate.handle)}.js`),signal);
            if(!eligible(current)) return null;
            const variant=current.variants.find(v=>v.available);
            if(!variant) return null;
            return {...current,...candidate,current,variant};
          }catch(error){if(signal.aborted) throw error;return null;}
        }));
        verified.push(...batch.filter(Boolean));
      }
      if(generation!==this.#generation || !this.isConnected)return;
      this.#cart=cart; this.#anchor=anchor;
      const products=diversify(verified,limit);
      this.hidden=!products.length;
      this.querySelector('[data-monzee-recommendation-list]').replaceChildren(...products.map(p=>this.#card(p)));
      this.setAttribute('data-ready','true');
    }catch(error){if(generation===this.#generation){this.hidden=true;if(error.name!=='AbortError')console.warn('monzee cart matching:',error.message);}}
    finally {if(generation===this.#generation)this.removeAttribute('data-loading');}
  }
  #card(product){
    const ja=(this.dataset.locale||'').startsWith('ja');
    const card=document.createElement('article'); card.className='monzee-cart-rec__card'; card.dataset.productId=product.id; card.dataset.category=product.category;
    const url=this.#route(`products/${encodeURIComponent(product.handle)}`);
    const link=document.createElement('a');link.className='monzee-cart-rec__image-link';link.href=url;link.setAttribute('aria-label',product.current.title);
    const source=product.current.featured_image?.src || product.current.featured_image || product.current.images?.[0];
    if(source){const img=document.createElement('img');img.src=source;img.alt=product.current.title;img.width=210;img.height=210;img.loading='lazy';img.className='monzee-cart-rec__image';link.append(img);}
    const info=document.createElement('div');info.className='monzee-cart-rec__information';
    const title=document.createElement('a');title.href=url;title.className='monzee-cart-rec__title';title.textContent=product.current.title;
    const price=document.createElement('span');price.className='monzee-cart-rec__price';
    price.textContent=new Intl.NumberFormat(this.dataset.locale||'ja',{style:'currency',currency:this.#cart.currency||this.dataset.currency||'JPY',maximumFractionDigits:(this.#cart.currency||this.dataset.currency)==='JPY'?0:2}).format(Number(product.variant.price)/100);
    info.append(title,price);
    if(product.current.variants.length>1 || product.requiresSizeCheck || product.category==='driver'){
      const details=document.createElement('a');details.className='monzee-cart-rec__add monzee-cart-rec__details';details.href=url;details.textContent=ja?'商品を確認する':'View product';info.append(details);
    }else{
      const button=document.createElement('button');button.type='button';button.className='monzee-cart-rec__add';button.textContent=this.dataset.addLabel||'Add to cart';button.addEventListener('click',()=>this.#add(product,button));info.append(button);
    }
    card.append(link,info);return card;
  }
  async #add(product,button){
    if(button.disabled)return;
    button.disabled=true;button.textContent=this.dataset.addingLabel||'Adding…';
    try{
      const current=await this.#fetchJson(this.#route(`products/${encodeURIComponent(product.handle)}.js`));
      const cart=await this.#fetchJson(this.#route('cart.js'));
      const variant=current.variants.find(v=>v.available && String(v.id)===String(product.variant.id));
      if(!eligible(current)||!variant||cart.items.some(p=>String(p.product_id)===String(product.id))){await this.refresh();return;}
      const sectionIds=[...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')].map(p=>p.dataset.sectionId).filter(Boolean))];
      const response=await fetch(this.#route('cart/add.js'),{method:'POST',headers:{'Content-Type':'application/json',Accept:'application/json'},body:JSON.stringify({items:[{id:Number(variant.id),quantity:1,properties:{_monzee_recommendation:'matching_test_v1',_monzee_reference_product:product.sourceId,_monzee_match_reason:product.reason}}],sections:sectionIds.join(','),sections_url:window.location.pathname})});
      const result=await response.json();if(!response.ok||result.status)throw new Error(result.description||result.message||'Unable to add product');
      const updated=await this.#fetchJson(this.#route('cart.js'));
      this.dispatchEvent(new CartAddEvent(updated,String(variant.id),{source:'monzee-cart-recommendations',itemCount:1,productId:String(product.id),variantId:String(variant.id),sections:result.sections}));
      document.querySelectorAll('monzee-cart-recommendations').forEach(p=>p.hydrate());
    }catch(error){
      this.dispatchEvent(new CartErrorEvent('monzee-cart-recommendations',error.message,error.message,null));
      button.textContent=this.dataset.errorLabel||'Please try again';
    }finally{button.disabled=false;if(button.isConnected)button.textContent=this.dataset.addLabel||'Add to cart';}
  }
}
if(!customElements.get('monzee-cart-recommendations'))customElements.define('monzee-cart-recommendations',MonzeeCartRecommendations);
for(const name of [EVENT_NAME,'cart:update'])document.addEventListener(name,()=>window.setTimeout(()=>document.querySelectorAll('monzee-cart-recommendations').forEach(p=>p.hydrate()),0));
